import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { CatalogModifierGroupDefinitionService } from '@features/catalog/services/catalog-modifier-group-definition.service';
import { CatalogModifierOptionDefinitionService } from '@features/catalog/services/catalog-modifier-option-definition.service';
import { ProductModifierDraftSelectionType } from '@features/catalog/models/product-form-draft.models';
import {
  ModifierGroupDefinitionMutationResult,
  ProductModifierGroupDefinition,
} from '@features/catalog/models/product-modifier-definition.models';
import { ModifierGroupOptionDraft } from '@features/catalog/models/modifier-group-option.models';

export interface ModifierGroupFormSaveRequest {
  readonly definitionId: string | null;
  readonly name: string;
  readonly slug: string | null;
  readonly description: string | null;
  readonly selectionType: ProductModifierDraftSelectionType;
  readonly required: boolean;
  readonly minSelections: number;
  readonly maxSelections: number | null;
  readonly position: number;
  readonly active: boolean;
  readonly baselineOptions: readonly ModifierGroupOptionDraft[];
  readonly draftOptions: readonly ModifierGroupOptionDraft[];
}

@Injectable()
export class ModifierGroupFormOrchestrationService {
  private readonly groups = inject(CatalogModifierGroupDefinitionService);
  private readonly options = inject(CatalogModifierOptionDefinitionService);

  save(request: ModifierGroupFormSaveRequest): Observable<ModifierGroupDefinitionMutationResult> {
    const payload = {
      name: request.name,
      slug: request.slug,
      description: request.description,
      selectionType: request.selectionType,
      required: request.required,
      minSelections: request.minSelections,
      maxSelections: request.maxSelections,
      position: request.position,
      active: request.active,
    };

    const group$ = request.definitionId
      ? this.groups.update({ id: request.definitionId, ...payload })
      : this.groups.create(payload);

    return group$.pipe(
      concatMap((result) => {
        if (result.errors.length || !result.definition) {
          return of(result);
        }
        return this.syncOptions(result.definition, request).pipe(
          map((errors) => ({
            definition: result.definition,
            errors,
          })),
        );
      }),
    );
  }

  private syncOptions(
    definition: ProductModifierGroupDefinition,
    request: ModifierGroupFormSaveRequest,
  ): Observable<string[]> {
    const groupId = definition.id;
    const baselineById = new Map(
      request.baselineOptions
        .filter((opt) => !!opt.id)
        .map((opt) => [opt.id as string, opt] as const),
    );
    const draftIds = new Set(
      request.draftOptions.map((opt) => opt.id).filter((id): id is string => !!id),
    );

    const namedDrafts = request.draftOptions.filter((opt) => opt.name.trim().length > 0);
    const creates = namedDrafts.filter((opt) => !opt.id);
    const updates = namedDrafts.filter((opt) => {
      if (!opt.id) {
        return false;
      }
      const baseline = baselineById.get(opt.id);
      if (!baseline) {
        return false;
      }
      return (
        baseline.name.trim() !== opt.name.trim() ||
        Number(baseline.priceDelta) !== Number(opt.priceDelta) ||
        (baseline.maxQuantity ?? null) !== (opt.maxQuantity ?? null)
      );
    });
    const removes = [...baselineById.keys()].filter((id) => !draftIds.has(id));

    type Step = () => Observable<string[]>;
    const steps: Step[] = [];

    creates.forEach((opt, index) => {
      const position = namedDrafts.indexOf(opt);
      steps.push(() =>
        this.options
          .create({
            productModifierGroupDefinitionId: groupId,
            name: opt.name.trim(),
            priceDelta: Math.max(0, Number(opt.priceDelta) || 0),
            maxQuantity: opt.maxQuantity,
            position: position >= 0 ? position : index,
            active: true,
          })
          .pipe(map((r) => r.errors)),
      );
    });

    updates.forEach((opt) => {
      steps.push(() =>
        this.options
          .update({
            id: opt.id as string,
            name: opt.name.trim(),
            priceDelta: Math.max(0, Number(opt.priceDelta) || 0),
            maxQuantity: opt.maxQuantity,
          })
          .pipe(map((r) => r.errors)),
      );
    });

    removes.forEach((id) => {
      steps.push(() => this.options.destroy(id).pipe(map((r) => r.errors)));
    });

    if (steps.length === 0) {
      return of([]);
    }

    return steps.reduce<Observable<string[]>>(
      (acc, step) =>
        acc.pipe(
          concatMap((prevErrors) => {
            if (prevErrors.length) {
              return of(prevErrors);
            }
            return step().pipe(map((next) => [...prevErrors, ...next]));
          }),
        ),
      of([]),
    );
  }
}
